import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Bell } from 'lucide-react';
import { apiGet, apiPut } from '../api';
import { toast } from 'react-toastify';

const NotificationBell = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [markingAll, setMarkingAll] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState({});
  const bellRef = useRef(null);
  const dropdownRef = useRef(null);

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiGet('/api/admin/notifications?page=1&pageSize=7&order=desc');
      if (res.data.success) {
        const { notifications: notifs = [], unreadCount: count, totalPages: pages } = res.data.data;
        setNotifications(notifs);
        setUnreadCount(count ?? notifs.filter(n => !n.isRead).length);
        setCurrentPage(1);
        setTotalPages(pages ?? 1);
      }
    } catch {
      // silent fail — badge stays as-is
    } finally {
      setLoading(false);
    }
  }, []);

  const loadMore = async () => {
    const nextPage = currentPage + 1;
    setLoadingMore(true);
    try {
      const res = await apiGet(`/api/admin/notifications?page=${nextPage}&pageSize=7&order=desc`);
      if (res.data.success) {
        const { notifications: notifs = [], totalPages: pages } = res.data.data;
        setNotifications(prev => [...prev, ...notifs]);
        setCurrentPage(nextPage);
        setTotalPages(pages ?? totalPages);
      }
    } catch {
      toast.error('Failed to load more notifications');
    } finally {
      setLoadingMore(false);
    }
  };

  // Initial fetch to populate unread badge
  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // Re-fetch every time dropdown opens
  useEffect(() => {
    if (isOpen) fetchNotifications();
  }, [isOpen, fetchNotifications]);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        dropdownRef.current && !dropdownRef.current.contains(e.target) &&
        bellRef.current && !bellRef.current.contains(e.target)
      ) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Close on scroll
  useEffect(() => {
    const handleScroll = () => setIsOpen(false);
    if (isOpen) window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isOpen]);

  const handleToggle = () => {
    if (!isOpen && bellRef.current) {
      const rect = bellRef.current.getBoundingClientRect();
      const width = Math.min(384, window.innerWidth - 24);
      const rightFromButton = window.innerWidth - rect.right;
      const leftEdge = window.innerWidth - rightFromButton - width;
      const right = leftEdge < 8
        ? window.innerWidth - width - 8
        : rightFromButton;
      setDropdownStyle({ top: rect.bottom + 8, right: Math.max(8, right), width });
    }
    setIsOpen(prev => !prev);
  };

  const markAsRead = async (id) => {
    try {
      const res = await apiPut(`/api/admin/notifications/${id}/read`, {});
      if (res.data.success) {
        setNotifications(prev =>
          prev.map(n => n._id === id ? { ...n, isRead: true } : n)
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch {
      toast.error('Failed to mark notification as read');
    }
  };

  const markAllAsRead = async () => {
    setMarkingAll(true);
    try {
      const res = await apiPut('/api/admin/notifications/read', {});
      if (res.data.success) {
        setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
        setUnreadCount(0);
      }
    } catch {
      toast.error('Failed to mark all as read');
    } finally {
      setMarkingAll(false);
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const diff = Math.floor((Date.now() - new Date(dateStr)) / 1000);
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  return (
    <>
      {/* Bell button */}
      <button
        ref={bellRef}
        onClick={handleToggle}
        className="relative flex items-center justify-center h-9 w-9 rounded-md border border-border/70 bg-white/[0.02] text-muted-foreground transition-colors hover:bg-white/[0.05] hover:text-foreground"
      >
        <Bell className="h-4 w-4" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white leading-none">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown — fixed so it escapes overflow:hidden parents */}
      {isOpen && (
        <div
          ref={dropdownRef}
          style={{
            position: 'fixed',
            top: dropdownStyle.top,
            right: dropdownStyle.right,
            width: dropdownStyle.width,
          }}
          className="rounded-lg border border-border/70 bg-[#0b0e11] shadow-2xl z-[9999] overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-border/50">
            <span className="text-sm font-medium text-foreground">Notifications</span>
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                disabled={markingAll}
                className="text-xs text-ginox-green hover:text-ginox-green/70 disabled:opacity-50 transition-colors"
              >
                {markingAll ? 'Marking...' : 'Mark all as read'}
              </button>
            )}
          </div>

          {/* List */}
          <div className="max-h-[400px] overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-10">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-ginox-green" />
              </div>
            ) : notifications.length === 0 ? (
              <p className="py-10 text-center text-sm text-muted-foreground">
                No notifications
              </p>
            ) : (
              <>
                {notifications.map(n => (
                  <div
                    key={n._id}
                    onClick={() => !n.isRead && markAsRead(n._id)}
                    className={`px-4 py-3 border-b border-border/30 transition-colors last:border-b-0 ${
                      n.isRead
                        ? 'opacity-50'
                        : 'cursor-pointer hover:bg-white/[0.03]'
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      {!n.isRead && (
                        <span className="mt-[5px] h-2 w-2 shrink-0 rounded-full bg-ginox-green" />
                      )}
                      <div className={`min-w-0 flex-1 ${n.isRead ? 'pl-4' : ''}`}>
                        <div className="flex items-start justify-between gap-2">
                          <p className={`text-xs font-medium truncate ${n.isRead ? 'text-muted-foreground' : 'text-foreground'}`}>
                            {n.title}
                          </p>
                          <span className="shrink-0 text-[10px] text-muted-foreground/60 mt-px">
                            {formatTime(n.createdAt)}
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                          {n.message}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
                {currentPage < totalPages && (
                  <div className="px-4 py-3 border-t border-border/30">
                    <button
                      onClick={loadMore}
                      disabled={loadingMore}
                      className="w-full text-xs text-ginox-green hover:text-ginox-green/70 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
                    >
                      {loadingMore ? (
                        <>
                          <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-ginox-green" />
                          Loading...
                        </>
                      ) : (
                        'Load more'
                      )}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default NotificationBell;
